import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { Box, List, Chip, Stack, Typography, Button } from "@mui/material";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import {
  useViewedTypeStore,
  useIdStore,
} from "../../stores/selectedToViewStore";
import { Family, FamilyStatus } from "../../interfaces/DTO/crm/family";
import { Contact } from "../../interfaces/DTO/crm/contact";
import SearchField from "../../shared/UIParts/SearchField";
import LoadingIndicator from "../../shared/UIParts/LoadingIndicator";
import ErrorAlert from "../../shared/UIParts/ErrorAlert";
import UserFamilyListItem from "../../shared/UIParts/UserFamilyListItem";
import { useContacts } from "../../hooks/crm/useContacts";
import { useFamilies } from "../../hooks/crm/useFamilies";
import { formatName } from "../../utils/namesFormater";
import { EntityFilterCategory, iconEntityFilterCategory, formatEntityFilterCategory, colorEntityFilterCategory } from "../../utils/featuresFormatter";
import { translateEnglishToHebrew, isEnglishText } from "../../utils/keyboardTranslationUtils";
import { ConsultingNavigationService } from "../../services/consultingNavigationService";

export const MiniConatctAndFamilyList: React.FC = () => {
  const { data: contacts, isLoading: isLoadingContacts, error: contactError } = useContacts();
  const { data: families, isLoading: isLoadingFamilies, error: familyError } = useFamilies();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const viewedType = useViewedTypeStore((state) => state.viewedType);
  const setViewedType = useViewedTypeStore((state) => state.setViewedType);
  const id = useIdStore((state) => state.id);
  const setId = useIdStore((state) => state.setId);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [category, setCategory] = useState<EntityFilterCategory>(EntityFilterCategory.All);

  const [localSelectedItem, setLocalSelectedItem] = useState<{ type: string; id: number }>({
    type: viewedType,
    id,
  });

  const effectiveSelected = useMemo(() => {
    return { type: viewedType, id };
  }, [viewedType, id]);

  useEffect(() => {
    if (localSelectedItem.type !== viewedType || localSelectedItem.id !== id) {
      setLocalSelectedItem({ type: viewedType, id: id || 0 });
    }
  }, [viewedType, id]);

  const refsMap = useRef(new Map<number, HTMLDivElement>());
  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      const selectedRef = refsMap.current.get(effectiveSelected.id);
      if (selectedRef) {
        selectedRef.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }, 100);
    return () => clearTimeout(timeout);
  }, [effectiveSelected.id, searchQuery]);

  // שלב 1: איחוד הרשימות (רק כשה-data משתנה)
  type CombinedItem = (Contact | Family) & { itemType: 'contact' | 'family' };
  const allItems = useMemo<CombinedItem[]>(() => {
    if (!contacts && !families) return [];

    const combined: CombinedItem[] = [
      ...(contacts?.map(c => ({ ...c, itemType: 'contact' as const })) || []),
      // סינון משפחות - לא להציג משפחות עם סטטוס CLOSED
      ...(families?.filter(f => f.status !== FamilyStatus.CLOSED).map(f => ({ ...f, itemType: 'family' as const })) || []),
    ];

    // מיון הרשימה המאוחדת לפי updated_at (פעם אחת בלבד)
    return combined.sort((a, b) => {
      const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [contacts, families]);

  // שלב 2: סינון הרשימה המאוחדת (מתבצע בכל פעם שמשתנה query או category)
  const filteredItems = useMemo(() => {
    const lowerCaseQuery = searchQuery.toLowerCase();

    // סינון לפי טקסט חיפוש
    let filtered = allItems.filter((item) => {
      if (!searchQuery) return true;

      if (item.itemType === 'contact') {
        const contact = item as Contact & { itemType: 'contact' };
        const fullName = formatName(contact);
        return (
          fullName.toLowerCase().includes(lowerCaseQuery) ||
          contact.phone?.toLowerCase().includes(lowerCaseQuery) ||
          contact.email?.toLowerCase().includes(lowerCaseQuery) ||
          contact.israeli_id?.toLowerCase().includes(lowerCaseQuery)
        );
      } else {
        const family = item as Family & { itemType: 'family' };
        return family.name.toLowerCase().includes(lowerCaseQuery);
      }
    });

    // סינון לפי קטגוריה
    switch (category) {
      case EntityFilterCategory.Contacts:
        filtered = filtered.filter((item) =>
          item.itemType === 'contact' && !!(item as Contact).last_data_reported_period
        );
        break;
      case EntityFilterCategory.Families:
        filtered = filtered.filter((item) => item.itemType === 'family');
        break;
      case EntityFilterCategory.HasQuestion:
        filtered = filtered.filter((item) =>
          item.itemType === 'contact' && (item as Contact).has_question
        );
        break;
      case EntityFilterCategory.RequiresAttention:
        filtered = filtered.filter((item) =>
          item.itemType === 'contact' && (item as Contact).requires_attention
        );
        break;
      case EntityFilterCategory.Favorite:
        filtered = filtered.filter((item) =>
          item.itemType === 'contact' && ((item as Contact).favorites_rank ?? 0) > 0
        );
        break;
      case EntityFilterCategory.PreAdviceOnly:
        filtered = filtered.filter((item) =>
          item.itemType === 'contact' &&
          !(item as Contact).last_data_reported_period &&
          !!(item as Contact).last_pre_advice_data_period
        );
        break;
      case EntityFilterCategory.Leads:
        filtered = filtered.filter((item) =>
          item.itemType === 'contact' &&
          !(item as Contact).last_data_reported_period &&
          !(item as Contact).last_pre_advice_data_period
        );
        break;
    }

    return filtered;
  }, [allItems, searchQuery, category]);

  // בדיקה אם יש להציג הצעת תרגום
  const shouldShowTranslationSuggestion = useMemo(() => {
    return searchQuery.trim() !== '' &&
           filteredItems.length === 0 &&
           isEnglishText(searchQuery);
  }, [searchQuery, filteredItems.length]);

  const translatedText = useMemo(() => {
    if (shouldShowTranslationSuggestion) {
      return translateEnglishToHebrew(searchQuery);
    }
    return '';
  }, [searchQuery, shouldShowTranslationSuggestion]);

  const handleSelect = (isContact: boolean, itemId: number, itemName: string) => {
    const newType = isContact ? "USER" : "FAMILY";
    if (localSelectedItem.type === newType && localSelectedItem.id === itemId) return;

    // עדכון ה-store
    setLocalSelectedItem({ type: newType, id: itemId });
    setViewedType(newType);
    setId(itemId);

    // עדכון URL והוספה להיסטוריה דרך ה-service המרכזי
    if (!ConsultingNavigationService.isPresentationMode()) {
      ConsultingNavigationService.pushToHistory({
        id: itemId,
        type: newType,
        preserveOtherParams: true,
      }, location.pathname);
    }
  };

  const handleTranslationSuggestionClick = () => {
    setSearchQuery(translatedText);
  };

  if (isLoadingContacts || isLoadingFamilies) return <LoadingIndicator />;
  if (contactError || familyError)
    return <ErrorAlert message={contactError?.message || familyError?.message || "Failed to load data"} />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, borderRadius: "8px", overflowY: "auto", height: "90%" }}>
      <Box sx={{ margin: "16px 16px -6px 16px" }}>
        <SearchField
          placeholder="חפש לפי שם, ת.ז, טלפון, אימייל ..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ px: 2, flexWrap: "wrap" }}>
        {Object.values(EntityFilterCategory).map((cat) => (
          <Chip
            key={cat}
            // icon={iconEntityFilterCategory(cat)}
            label={formatEntityFilterCategory(cat)}
            clickable
            onClick={() => setCategory(cat)}
            sx={{
              color: category === cat ? "#fff" : undefined,
              backgroundColor: category === cat ? colorEntityFilterCategory(cat) : undefined,
            }}
            variant={category === cat ? "filled" : "outlined"}
          />
        ))}
      </Stack>

      <List sx={{ height: "100%", overflowY: "auto", marginLeft: "6px" }}>
        {filteredItems.map((item) => {
          const isContact = "first_name" in item;
          const itemId = isContact ? (item as Contact).id! : (item as Family).id;
          const isSelected =
            localSelectedItem.type === (isContact ? "USER" : "FAMILY") &&
            localSelectedItem.id === itemId;

          return (
            <div
              key={`${isContact ? "contact" : "family"}-${itemId}`}
              ref={(el) => {
                if (el) refsMap.current.set(itemId, el);
              }}
            >
              <UserFamilyListItem
                item={item}
                isSelected={isSelected}
                onSelect={handleSelect}
              />
            </div>
          );
        })}
        
        {/* הצגת הצעת תרגום */}
        {shouldShowTranslationSuggestion && (
          <Box sx={{ padding: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              האם התכוונת ל
            </Typography>
            <Button
              onClick={handleTranslationSuggestionClick}
              sx={{
                color: '#1976d2',
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  textDecoration: 'underline',
                },
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              {translatedText}
            </Button>
          </Box>
        )}
      </List>
    </Box>
  );
};